import { useContext, useEffect, useRef, useState } from "react"
import type { ChangeEvent } from "react"
import { useLocation } from "react-router-dom"
import { useNavigate } from "react-router-dom"
import { useShallow } from "zustand/react/shallow"

import { IMainPageUI } from "./types"
import { MainPageUI } from "./ui"

import { useMain } from "@api/main/state"
import { pingSession } from "@api/login/api"
import { isAuthenticationRejected, useLogin } from "@api/login/state"

import { useTheme } from "@hooks/useTheme"
import { useStorageHealthWarnings } from "@hooks/useStorageHealthWarnings"
import { useValue } from "@hooks/useValue"
import applicationNotificationSoundService from "@services/applicationNotificationSound.service"
import { getCurrentClientReleaseSignature, getLatestClientReleaseSignature } from "@services/client-release.service"
import pushNotificationsService, { PushNotificationPayload, PushNotificationState, PushNotificationStatus } from "@services/pushNotifications.service"
import { getCurrentRoutePageMeta } from "@services/route-page.service"
import realtimeService from "@services/realtime.service"
import { reportUnexpectedClientError } from "@services/client-error.service"

import { DrawerMenu, IDrawerMenu } from "@primitive/drawer.menu"
import { IModalConfirm, ModalConfirm } from "@primitive/modal.confirm"
import { Loader, Toogle } from "@ui/index"
import { NotificationContext, notifyOpen } from "@ui/notification/notification.provider"


const RELEASE_CHECK_INTERVAL_MS = 60000
const RELEASE_REMINDER_INTERVAL_MS = 5 * 60 * 1000
const ACTIVITY_REPORT_INTERVAL_MS = 30000
const SESSION_RESTORE_RETRY_MS = 5000
const RELEASE_STARTUP_REFRESH_KEY = 'crm_release_startup_refresh_signature'
const RELEASE_REFRESH_QUERY_PARAM = 'crmReleaseRefresh'
const PUSH_PROMPT_DEFERRED_KEY = 'crm_push_notifications_prompt_deferred'
const PUSH_PROMPT_DISMISSED_KEY = 'crm_push_notifications_prompt_dismissed'

type NewApplicationRealtimePayload = PushNotificationPayload & {
    reason?: string
    applicationIDs?: number[]
    isRepeat?: boolean
}

const PushNotificationsPromptContent = ({
    status,
    onDoNotRemindChange,
}: {
    status: PushNotificationStatus
    onDoNotRemindChange: (value: boolean) => void
}) => {
    const [doNotRemind, setDoNotRemind] = useState(false)

    const handleDoNotRemindChange = (event: ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked

        setDoNotRemind(isChecked)
        onDoNotRemindChange(isChecked)
    }

    return (
        <div style={{display: 'grid', gap: '12px', fontSize: '15px', letterSpacing: '1.5px', lineHeight: '140%'}}>
            {status === 'denied' ? (
                <>
                    <p style={{margin: 0}}>В браузере уведомления для CRM сейчас выключены.</p>
                    <p style={{margin: 0}}>Откройте настройки сайта возле адресной строки и включите переключатель «Уведомления», затем обновите страницу CRM.</p>
                </>
            ) : (
                <>
                    <p style={{margin: 0}}>CRM может показывать системные уведомления о новых заявках даже когда вкладка не на экране.</p>
                    <p style={{margin: 0}}>Нажмите «Включить», затем подтвердите разрешение в браузере.</p>
                </>
            )}

            <label style={{display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: '12px', marginTop: '2px', padding: '10px 12px', borderRadius: '8px', background: 'var(--background_secondary)'}}>
                <span>Больше не напоминать</span>
                <Toogle checked={doNotRemind} onChange={handleDoNotRemindChange} />
            </label>
        </div>
    )
}


/** Контейнер - Главная страница */
export const MainContainer = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const notify = useContext(NotificationContext)
    const { theme, toggleTheme } = useTheme()

    // Хранилище страницы - Главная
    const { countUnverifiedWorks, countNewApplications, countDelayedActionExpiredApplications, actionGetCountInfoApp } = useMain(useShallow((state) => ({
        countNewApplications: state.badge.countNewApplications,
        countDelayedActionExpiredApplications: state.badge.countDelayedActionExpiredApplications,
        countUnverifiedWorks: state.badge.countUnverifiedWorks,
        actionGetCountInfoApp: state.actionGetCountInfoApp,
    })))

    // Хранилище страницы - Авторизация
    const { isAuthorized, accessLevelID, actionLogout, actionCheckAuth, actionGetUserProfile } = useLogin(useShallow((state) => ({
        isAuthorized: state.isAuthorized,
        accessLevelID: state.profile.accessLevelID,
        actionLogout: state.actionLogout,
        actionCheckAuth: state.actionCheckAuth,
        actionGetUserProfile: state.actionGetUserProfile,
    })))
    const storageWarnings = useStorageHealthWarnings(isAuthorized && accessLevelID === 1)

    /** Интервал для автоматического обновления информации BADGE */
    const intervalRef = useRef<NodeJS.Timeout | null>(null)
    /** Интервал для heartbeat активной сессии */
    const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null)
    /** Таймер повторной проверки сессии после временной ошибки */
    const sessionRestoreTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    /** Время последней отправки реальной активности оператора */
    const lastActivityReportAtRef = useRef(0)
    /** Интервал проверки новой версии клиента */
    const releaseCheckIntervalRef = useRef<NodeJS.Timeout | null>(null)
    /** Таймер повторного напоминания об обновлении */
    const releaseReminderTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    /** Сигнатура текущей версии клиента */
    const currentReleaseSignatureRef = useRef<string | null>(null)
    /** Сигнатура найденной новой версии клиента */
    const pendingReleaseSignatureRef = useRef<string | null>(null)
    /** Флаг первой проверки релиза после запуска CRM */
    const isInitialReleaseCheckRef = useRef(true)
    /** Флаг открытой модалки обновления */
    const isReleaseModalOpenRef = useRef(false)
    /** Флаг активной проверки новой версии */
    const isReleaseCheckInProgressRef = useRef(false)
    /** Выбор пользователя не показывать повторно окно Push */
    const pushNotificationsDoNotRemindRef = useRef(false)
    /** Заголовок в header (Наименование секции) */
    const sectioName = useValue<string>('Заявки')
    
    /** Основное меню для МОБИЛОК */
    const drawerMenu = useValue<IDrawerMenu>({isOpen: false})
    /** Окно подтверждения */
    const modalConfirm = useValue<IModalConfirm>({isOpen: false})
    /** Окно предложения обновить релиз */
    const releaseModalConfirm = useValue<IModalConfirm>({
        isOpen: false,
        cancelButtonText: 'ПОЗЖЕ',
        okButtonText: 'ОБНОВИТЬ',
    })
    /** Окно предложения включить системные уведомления */
    const pushNotificationsModalConfirm = useValue<IModalConfirm>({isOpen: false})

    const isPushNotificationsPromptDismissed = () => {
        try {
            return localStorage.getItem(PUSH_PROMPT_DISMISSED_KEY) === '1'
        } catch {
            return false
        }
    }

    const deferPushNotificationsPrompt = () => {
        sessionStorage.setItem(PUSH_PROMPT_DEFERRED_KEY, '1')

        if (!pushNotificationsDoNotRemindRef.current) return

        try {
            localStorage.setItem(PUSH_PROMPT_DISMISSED_KEY, '1')
        } catch {}
    }

    const sendSessionPing = (isActivity: boolean) => {
        pingSession({...getCurrentRoutePageMeta(), isActivity}).catch((res) => {
            reportUnexpectedClientError(res, 'Main.pingSession')
            if (res.status === 500) {
                notify && notifyOpen(res.message, 'error', 2000, notify)
            }
        })
    }

    const sendPresenceHeartbeat = (forceRealtime = false) => {
        if (realtimeService.isConnected()) {
            if (forceRealtime) realtimeService.sendPresenceHeartbeat()
            return
        }

        sendSessionPing(false)
    }

    const reportUserActivity = (force = false) => {
        const now = Date.now()

        if (!force && now - lastActivityReportAtRef.current < ACTIVITY_REPORT_INTERVAL_MS) return

        lastActivityReportAtRef.current = now
        sendSessionPing(true)
    }

    const clearReleaseReminder = () => {
        if (releaseReminderTimeoutRef.current) {
            clearTimeout(releaseReminderTimeoutRef.current)
            releaseReminderTimeoutRef.current = null
        }
    }

    const closeReleaseModal = () => {
        isReleaseModalOpenRef.current = false
        releaseModalConfirm.setValue((prevValue) => ({...prevValue, isOpen: false}))
    }

    const scheduleReleaseReminder = () => {
        clearReleaseReminder()

        releaseReminderTimeoutRef.current = setTimeout(() => {
            if (pendingReleaseSignatureRef.current) {
                openReleaseModal()
            }
        }, RELEASE_REMINDER_INTERVAL_MS)
    }

    const handleRefreshRelease = () => {
        clearReleaseReminder()
        pendingReleaseSignatureRef.current = null
        window.location.reload()
    }

    const clearStartupReleaseRefreshState = () => {
        sessionStorage.removeItem(RELEASE_STARTUP_REFRESH_KEY)
    }

    const refreshStaleStartupRelease = (latestReleaseSignature: string) => {
        const refreshedReleaseSignature = sessionStorage.getItem(RELEASE_STARTUP_REFRESH_KEY)
        if (refreshedReleaseSignature === latestReleaseSignature) return false

        sessionStorage.setItem(RELEASE_STARTUP_REFRESH_KEY, latestReleaseSignature)

        const refreshUrl = new URL(window.location.href)
        refreshUrl.searchParams.set(RELEASE_REFRESH_QUERY_PARAM, Date.now().toString())
        window.location.replace(refreshUrl.toString())

        return true
    }

    const openReleaseModal = () => {
        clearReleaseReminder()
        isReleaseModalOpenRef.current = true

        releaseModalConfirm.setValue({
            isOpen: true,
            maxWidth: '520px',
            containerClassName: 'modal_container_release',
            headerText: 'Доступно обновление ЦРМ',
            content: (
                <div className="release_update">
                    <p className="release_update-title">На сервере опубликована новая версия ЦРМ.</p>
                    <p className="release_update-text">Чтобы получить последние изменения и работать на актуальной версии, необходимо обновить страницу.</p>
                    <div className="release_update-note">
                        <span className="release_update-note-title">Если сейчас неудобно</span>
                        <p>Нажмите «Позже», и мы напомним через 5 минут.</p>
                    </div>
                </div>
            ),
            cancelButtonText: 'ПОЗЖЕ',
            okButtonText: 'ОБНОВИТЬ СЕЙЧАС',
            handleButtonCancel() {
                closeReleaseModal()
                scheduleReleaseReminder()
            },
            handleButtonOk() {
                handleRefreshRelease()
            }
        })
    }

    const clearReleaseWatchers = () => {
        if (releaseCheckIntervalRef.current) {
            clearInterval(releaseCheckIntervalRef.current)
            releaseCheckIntervalRef.current = null
        }

        clearReleaseReminder()

        currentReleaseSignatureRef.current = null
        pendingReleaseSignatureRef.current = null
        isInitialReleaseCheckRef.current = true
        isReleaseModalOpenRef.current = false
        isReleaseCheckInProgressRef.current = false

        releaseModalConfirm.setValue((prevValue) => ({...prevValue, isOpen: false}))
    }

    const checkClientRelease = async () => {
        if (process.env.NODE_ENV !== 'production' || isReleaseCheckInProgressRef.current) return

        isReleaseCheckInProgressRef.current = true
        const isInitialReleaseCheck = isInitialReleaseCheckRef.current

        try {
            currentReleaseSignatureRef.current = currentReleaseSignatureRef.current || getCurrentClientReleaseSignature()

            if (!currentReleaseSignatureRef.current) return

            const latestReleaseSignature = await getLatestClientReleaseSignature()

            if (!latestReleaseSignature) return

            if (latestReleaseSignature === currentReleaseSignatureRef.current) {
                pendingReleaseSignatureRef.current = null
                clearStartupReleaseRefreshState()
                clearReleaseReminder()
                return
            }

            if (isInitialReleaseCheck && refreshStaleStartupRelease(latestReleaseSignature)) return

            const hasNewRelease = pendingReleaseSignatureRef.current !== latestReleaseSignature

            pendingReleaseSignatureRef.current = latestReleaseSignature

            if (isReleaseModalOpenRef.current) return

            if (hasNewRelease || !releaseReminderTimeoutRef.current) {
                openReleaseModal()
            }
        } finally {
            isInitialReleaseCheckRef.current = false
            isReleaseCheckInProgressRef.current = false
        }
    }

    /** Предлагает включить Web Push при старте CRM, если уведомления ещё не включены. */
    const checkPushNotificationsPrompt = async (currentState?: PushNotificationState) => {
        if (isPushNotificationsPromptDismissed()) return
        if (sessionStorage.getItem(PUSH_PROMPT_DEFERRED_KEY) === '1') return
        if (pushNotificationsModalConfirm.value.isOpen) return

        const state = currentState || await pushNotificationsService.getStatus()
        if (state.status === 'enabled' || state.status === 'unsupported') return

        pushNotificationsDoNotRemindRef.current = false

        pushNotificationsModalConfirm.setValue({
            isOpen: true,
            maxWidth: '520px',
            headerText: state.status === 'denied' ? 'Уведомления выключены в браузере' : 'Включить уведомления о новых заявках?',
            content: <PushNotificationsPromptContent status={state.status} onDoNotRemindChange={(value) => { pushNotificationsDoNotRemindRef.current = value }} />,
            cancelButtonText: 'ПОЗЖЕ',
            okButtonText: state.status === 'denied' ? 'ПОНЯТНО' : 'ВКЛЮЧИТЬ',
            handleButtonCancel() {
                deferPushNotificationsPrompt()
                pushNotificationsModalConfirm.setValue((prevValue) => ({...prevValue, isOpen: false}))
            },
            handleButtonOk() {
                if (state.status === 'denied') {
                    deferPushNotificationsPrompt()
                    pushNotificationsModalConfirm.setValue((prevValue) => ({...prevValue, isOpen: false}))
                    return
                }

                if (pushNotificationsDoNotRemindRef.current) deferPushNotificationsPrompt()

                pushNotificationsService.enable().then((result) => {
                    notify && notifyOpen(result.message, result.isError ? 'error' : 'success', 3000, notify)
                    if (result.isEnabled) sessionStorage.setItem(PUSH_PROMPT_DEFERRED_KEY, '1')
                }).finally(() => {
                    pushNotificationsModalConfirm.setValue((prevValue) => ({...prevValue, isOpen: false}))
                })
            }
        })
    }

    const getApplicationNotificationKey = (payload: NewApplicationRealtimePayload) => {
        if (payload.applicationID) return `application:${payload.applicationID}`
        if (payload.applicationIDs?.length) return `application:${payload.applicationIDs.join('-')}`

        return `application:${Date.now()}`
    }

    const playApplicationNotificationSound = (payload: NewApplicationRealtimePayload) => {
        applicationNotificationSoundService.play(getApplicationNotificationKey(payload), payload.soundUrl)
    }

    const showApplicationNotificationFallback = (payload: NewApplicationRealtimePayload) => {
        const applicationID = payload.applicationID || payload.applicationIDs?.[0]
        const applicationsCount = payload.applicationIDs?.length || 0

        void pushNotificationsService.showApplicationNotificationFallback({
            ...payload,
            applicationID,
            title: payload.title || 'ЦРМ Система',
            body: payload.body || (applicationsCount > 1
                ? `Новые заявки в CRM: ${applicationsCount}`
                : applicationID
                    ? `Поступила ${payload.isRepeat ? 'повторная' : 'новая'} заявка №${applicationID}.`
                    : 'Новая заявка в CRM.'),
            url: payload.url || (applicationID ? `/lk/applications/${applicationID}` : '/lk/applications'),
        }).catch((error) => reportUnexpectedClientError(error, 'Main.showApplicationNotificationFallback'))
    }

    const handleApplicationsRealtimeEvent = (event: { payload?: unknown }) => {
        const payload = event.payload as NewApplicationRealtimePayload | undefined

        if (payload?.reason !== 'newApplication') return

        playApplicationNotificationSound(payload)
        showApplicationNotificationFallback(payload)
    }

    const handleServiceWorkerMessage = (event: MessageEvent) => {
        const payload = event.data as PushNotificationPayload & { type?: string }

        if (payload?.type === 'crm-notification-click') {
            const targetUrl = new URL(payload.url || (payload.applicationID ? `/lk/applications/${payload.applicationID}` : '/lk/applications'), window.location.origin)
            if (targetUrl.origin !== window.location.origin) return

            navigate(`${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`)
            return
        }

        if (payload?.type !== 'crm-push-notification') return

        playApplicationNotificationSound({ applicationID: payload.applicationID, soundUrl: payload.soundUrl })
        showApplicationNotificationFallback(payload)
    }
    
    // ==========================================================

    useEffect(() => {
        let isCancelled = false

        if (!isAuthorized) {
            const restoreSession = async () => {
                try {
                    const response = await actionCheckAuth()
                    if (isCancelled || response.status === 200) return

                    if (isAuthenticationRejected(response.status)) {
                        navigate('/')
                        return
                    }

                    sessionRestoreTimeoutRef.current = setTimeout(restoreSession, SESSION_RESTORE_RETRY_MS)
                } catch (error) {
                    reportUnexpectedClientError(error, 'Main.actionCheckAuth')
                    if (!isCancelled) {
                        sessionRestoreTimeoutRef.current = setTimeout(restoreSession, SESSION_RESTORE_RETRY_MS)
                    }
                }
            }

            void restoreSession()
        } else {
            actionGetCountInfoApp()
            actionGetUserProfile()
            pushNotificationsService.syncCurrentSubscription()
                .then((state) => checkPushNotificationsPrompt(state))
                .catch((error) => {
                    reportUnexpectedClientError(error, 'Main.syncCurrentSubscription')
                    checkPushNotificationsPrompt()
                })

            currentReleaseSignatureRef.current = getCurrentClientReleaseSignature()
            checkClientRelease()

            const unsubscribeBadges = realtimeService.subscribe('badges', () => {
                actionGetCountInfoApp(true).catch((res) => {
                    reportUnexpectedClientError(res, 'Main.realtimeGetCountInfoApp')
                    if (res.status === 500) notify && notifyOpen(res.message, 'error', 2000, notify)
                })
            })

            const unsubscribeApplications = realtimeService.subscribe('applications', handleApplicationsRealtimeEvent)

            const unlockNotificationSound = () => {
                applicationNotificationSoundService.unlock()
            }

            window.addEventListener('pointerdown', unlockNotificationSound, { once: true })
            window.addEventListener('keydown', unlockNotificationSound, { once: true })
            navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage)

            intervalRef.current = setInterval(() => {
                actionGetCountInfoApp(true).catch((res) => {
                    reportUnexpectedClientError(res, 'Main.intervalGetCountInfoApp')
                    if (res.status === 500) notify && notifyOpen(res.message, 'error', 2000, notify)
                })
            }, 60000)

            heartbeatIntervalRef.current = setInterval(() => {
                sendPresenceHeartbeat()
            }, 30000)

            releaseCheckIntervalRef.current = setInterval(() => {
                checkClientRelease()
            }, RELEASE_CHECK_INTERVAL_MS)

            const handlePageResume = () => {
                if (document.visibilityState === 'visible') {
                    sendPresenceHeartbeat(true)
                    reportUserActivity(true)
                    checkClientRelease()
                }
            }

            const handleWindowFocus = () => {
                sendPresenceHeartbeat(true)
                reportUserActivity(true)
                checkClientRelease()
            }

            const handleUserActivity = () => reportUserActivity()

            document.addEventListener('visibilitychange', handlePageResume)
            window.addEventListener('focus', handleWindowFocus)
            window.addEventListener('pointerdown', handleUserActivity)
            window.addEventListener('keydown', handleUserActivity)

            return () => {
                document.removeEventListener('visibilitychange', handlePageResume)
                window.removeEventListener('focus', handleWindowFocus)
                window.removeEventListener('pointerdown', handleUserActivity)
                window.removeEventListener('keydown', handleUserActivity)
                window.removeEventListener('pointerdown', unlockNotificationSound)
                window.removeEventListener('keydown', unlockNotificationSound)
                navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage)
                unsubscribeBadges()
                unsubscribeApplications()

                if (intervalRef.current) {
                    clearInterval(intervalRef.current)
                    intervalRef.current = null
                }

                if (heartbeatIntervalRef.current) {
                    clearInterval(heartbeatIntervalRef.current)
                    heartbeatIntervalRef.current = null
                }

                clearReleaseWatchers()
            }
        }

        return () => {
            isCancelled = true

            if (sessionRestoreTimeoutRef.current) {
                clearTimeout(sessionRestoreTimeoutRef.current)
                sessionRestoreTimeoutRef.current = null
            }

            if (intervalRef.current) {
                clearInterval(intervalRef.current)
                intervalRef.current = null
            }

            if (heartbeatIntervalRef.current) {
                clearInterval(heartbeatIntervalRef.current)
                heartbeatIntervalRef.current = null
            }

            clearReleaseWatchers()
        }        
    }, [isAuthorized]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isAuthorized) return

        sendPresenceHeartbeat(true)
        reportUserActivity(true)
    }, [isAuthorized, location.pathname, location.search, location.hash]) // eslint-disable-line react-hooks/exhaustive-deps
    

    // ==========================================================

    /** Открытие/закрытие окна подтверждения */
    const handleOpenModalConfirm = (isOpen: boolean, headerText: string, contentText: string) => {
        modalConfirm.setValue({
            isOpen, headerText, contentText, 
            
            handleButtonCancel() {
                modalConfirm.setValue({isOpen: false})
            }, 
            
            handleButtonOk() {
                actionLogout()
            }
        })
    }

    /** Открытие/закрытие главного мобильного меню */
    const handleOpenDrawerMenu = (isOpen: boolean) => {
        drawerMenu.setValue({
            isOpen, 
            
            handleButtonCancel() {
                drawerMenu.setValue({isOpen: false})
            }, 
            
            handleOpenModalExitProgram() {
                handleOpenModalConfirm(true, 'Выход из системы...', 'Вы действительно хотите выйти из приложения?')
            }
        })
    }

    // ==========================================================

    /** Свойства передавемые в UI Главной страницы */
    const propsToUI: IMainPageUI = {
        sectioName,

        countNewApplications,
        countDelayedActionExpiredApplications,
        countUnverifiedWorks,
        storageWarnings,

        theme,
        toggleTheme,

        handleOpenDrawerMenu,
        handleOpenModalConfirm,
    }

    return <>
        <Loader isOpen={!isAuthorized} title='ЗАГРУЗКА' />
        {isAuthorized && <MainPageUI {...propsToUI} />}

        <DrawerMenu {...drawerMenu.value} />
        <ModalConfirm {...modalConfirm.value} />
        <ModalConfirm {...pushNotificationsModalConfirm.value} />
        <ModalConfirm {...releaseModalConfirm.value} />
    </>
}